<template>
  <div>
    <h1>{{ $t('views.reservationConfirmation.title') }}</h1>

    <div v-if="deletedFeedback" class="col-12 teres-alert bg-lightblue my-3" role="alert">
      <feedback-message :message="$t('views.reservationConfirmation.success')"  />
    </div>

    <loading-container :state="state" i18nErrorMessage="views.reservationConfirmation.errorMessage">
      <ul class="row list-unstyled my-3 mx-auto p-inline-start-0" v-if="tickets.length > 0">
        <li v-for="(ticket, index) in tickets" :key="'ticket-' + index" class="col-12 mb-3">
          <div class="d-flex row reservation-overview no-underline">
            <div class="col-12 col-md-3 p-2 px-4 bg-darker bg-hover-dark font-weight-bold d-flex align-items-center">
              {{ formatDay(ticket) }}<br>
              {{ formatTimeOnly(ticket) }}
            </div>
            <div class="col-12 col-md-9 d-flex align-items-center flex-md-row flex-column">
              <div class="saal-desc p-2">
                <h2 class="m-0">{{ ticket.roomName }}</h2>
                <p class="mb-1">
                  {{ ticket.roomDescription }}
                </p>
              </div>
              <div class="ml-auto mb-2 mb-md-0">
                <button-cancel v-if="isRemoveable(ticket)" v-on:clicked="removeTicket(ticket)" :i18nMessage="$t('views.reservationConfirmation.cancel')" class="btn-outline-icon-only btn-outline-icon-text-red" />
              </div>
            </div>
          </div>
        </li>
      </ul>

      <div v-if="tickets.length === 0 && deletedFeedback" class="col-12 teres-alert bg-lightblue my-3">
        <feedback-message :message="$t('views.reservationConfirmation.noTickets')" />
      </div>

      <div v-if="tickets.length === 0 && invalidOwnerId && deletedFeedback === false" class="col-12 teres-alert bg-lightblue my-3">
        <feedback-message :message="$tc('views.reservationConfirmation.invalidOwnerId', { msg: ownerId })" />
      </div>

      <link-arrow i18nMessage="views.reservationConfirmation.newReservation" to="/" />
    </loading-container>
  </div>
</template>

<script lang="ts">
import FeedbackMessage from '@/components/feedback/FeedbackMessage.vue'
import ButtonCancel from '@/components/interaction/ButtonCancel.vue'
import LinkArrow from '@/components/interaction/LinkArrow.vue'
import LoadingContainer from '@/components/structure/LoadingContainer.vue'
import { Room, Ticket } from '@/generated/openapi'
import LoadingMixin from '@/mixins/LoadingMixin'
import { userService } from '@/services/userService'
import { formatTicketTimeDay, formatTicketTimeOnly } from '@/utils/formatUtils'
import { LoadingState } from '@/utils/loadingUtils'
import { urlParamString } from '@/utils/navigationUtils'
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'ReservationConfirmation',
  mixins: [
    LoadingMixin
  ],
  data() {
    return {
      room: {} as Room,
      tickets: [] as Ticket[],
      ownerId: '',
      invalidOwnerId: false,
      deletedFeedback: false
    }
  },
  components: {
    LoadingContainer,
    LinkArrow,
    FeedbackMessage,
    ButtonCancel
  },
  created() {
    this.ownerId = urlParamString(this.$route, 'id')
    this.loadFromServer()
  },
  methods: {
    loadFromServer() {
      this.state = LoadingState.loading;
      userService.getTicketsForReservationId(this.ownerId).then(resp => {
        this.tickets = resp.data
        this.state = LoadingState.success
      }).catch(err => {
        this.tickets = []
        if (err && err.response && err.response.status === 404) {
          this.invalidOwnerId = true
          this.state = LoadingState.success
        } else {
          this.state = LoadingState.error
        }
      })
    },
    removeTicket(ticket: Ticket) {
      this.state = LoadingState.loading;
      userService.removeTicketsFromReservation(this.ownerId, [ticket.id])
        .then(() => {
          this.deletedFeedback = true
          this.loadFromServer()
        }).catch(this.handleError)
    },
    formatDay(ticket: Ticket){
      return formatTicketTimeDay(ticket)
    },
    formatTimeOnly(ticket: Ticket){
      return formatTicketTimeOnly(ticket);
    },
    isRemoveable(ticket: Ticket) {
      return !!ticket.cancelable
    }
  }
});
</script>
